import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { handleValidateCodevalue, telecallingProviders } from '../Utility/constant';
import { StyledConfigurationContainer, StyledLeadContainer, StyledSubHeader } from './Styled';
import BiztantuLoader from './Loader';
import { Box, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import tableBodyicon from '../Assets/images/tablebody_edit.png';
import tableEditicon from '../Assets/images/tableheader_edit.png';
import disabledEdit from '../Assets/images/disabled_edit.png';
import DataGridTable from './DataGrid';
import { useSnackbar } from 'notistack';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import { requestHideLoader, requestShowLoader } from '../Store/Global/action';
import { createRequestData, updateRequestData } from '../Utility/apiHelper';
import SelectBoxWithLabel from './SelectBoxWithLable';
import { requestGetTelecallingDetails } from '../Store/Telecalling/action';
import '../Pages/Template/template.css';

const TelecallingSettings = () => {
  const isLoading = useSelector(state => state?.global?.isLoading);
  const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);
  const specificRolePermission = useSelector(state => state?.owner?.rolebasedData);

  const [dataGridData, setDatagridData] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const telecallingDetails = useSelector(state=> state?.telecalling?.telecallingDetails?.[0]);
    
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const canEdit = specificRolePermission?.isNotificationManage;

const createTelecallingRow = (data = {}) => ({
  id: data?.id ?? Date.now(),
  provider: data?.provider ?? "",
  accountSid: data?.accountSid ?? "",
  authToken: data?.authToken ?? "",
  apiKey: data?.apiKey ?? "",
  apiSecret: data?.apiSecret ?? "",
  twimlAppSid: data?.twimlAppSid ?? "",
  phoneNumber: data?.phoneNumber ?? "",
  isNew: !data?.id,
});

useEffect(() => {
  try {
    if (telecallingDetails) {
      setDatagridData([createTelecallingRow(telecallingDetails)]);
    } else {
      setDatagridData([createTelecallingRow()]);
    }
  } catch (error) {
    console.error("Error mapping telecalling details:", error);
    setDatagridData([createTelecallingRow()]);
  }
}, [telecallingDetails]);

  const handleEditClick = useCallback((id) => () => {
    setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleSaveClick = useCallback((id) => () => {
    setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback((id) => () => {
    setRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  }, []);

  const processRowUpdate = useCallback(async (newRow, oldRow) => {
    if (
        !newRow?.provider ||
        !newRow?.accountSid ||
        !newRow?.authToken  ||
        !newRow?.apiKey ||
        !newRow?.apiSecret ||
        !newRow?.twimlAppSid ||
        !newRow?.phoneNumber
        ) {
      enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"),{ variant: "error" });
      handleEditClick(oldRow?.id)()
      return oldRow;
    }
    try {
      dispatch(requestShowLoader());

      const payload = {
        provider: newRow.provider?.trim(),
        accountSid: newRow.accountSid?.trim(),
        authToken: newRow.authToken?.trim(),
        apiKey: newRow.apiKey?.trim(),
        apiSecret: newRow.apiSecret?.trim(),
        twimlAppSid: newRow.twimlAppSid?.trim(),
        phoneNumber: newRow.phoneNumber?.trim(),
      };

      let response;
      const baseUrl = `${process.env.REACT_APP_BASE_URL}api/telecalling`;

      if (newRow.isNew) {
        response = await createRequestData(payload, baseUrl);
      } else {
        response = await updateRequestData(payload, `${baseUrl}/${newRow.id}`);
      }

      if (response?.status === 200 || response?.status === 201) {
        enqueueSnackbar(handleValidateCodevalue(internationalise, (response?.data?.message || "TC_CRED_SAVED_SUCCESSFULLY")), { variant: "success" });
                            
        const updatedRow = {
          ...newRow,
          id: response?.data?.id || newRow.id,
          isNew: false,
        };

        setDatagridData(prev =>
          prev.map(row => (row.id === newRow.id ? updatedRow : row))
        );
      dispatch(requestGetTelecallingDetails())
        return updatedRow;
      }
      else{
        enqueueSnackbar(handleValidateCodevalue(internationalise, (response?.propererror.data?.message || "COMMON_PLEASE_TRY_AGAIN")), { variant: "error" });
        handleCancelClick(oldRow?.id)()
        return oldRow;
      }
    } catch (error) {
        handleCancelClick(oldRow?.id)()
        return oldRow;  
    } finally {
      dispatch(requestHideLoader());
    }
  },[dispatch, enqueueSnackbar, internationalise, handleEditClick]);

  const columns = useMemo(() => [
    {
      field: 'actions',
      type: 'actions',
      headerName: (
        <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_ACTION')}>
          <img src={tableEditicon} className="provider-settings-edit-icon" />
        </Tooltip>
      ),
      width: 100,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<CheckIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<img src={!canEdit ? disabledEdit : tableBodyicon} />}
            label="Edit"
            onClick={handleEditClick(id)}
            disabled={!canEdit}
          />,
        ];
      },
    },
    {
        field: "provider",
        headerName: handleValidateCodevalue(internationalise, 'TC_COLUMN_PROVIDER'),
        flex: 1,
        editable: true,
        renderEditCell: (params) => (
        <SelectBoxWithLabel
          required
          name="provider"
          value={params.value || ""}
          optionMenuItem={telecallingProviders}
          handleOnChange={(event) => {
            params.api.setEditCellValue({
              id: params.id,
              field: "provider",
              value: event.target.value,
            });
          }}
        />
      ),
    },
    {
        field: "accountSid",
        headerName: handleValidateCodevalue(internationalise, 'TC_COLUMN_ACCOUNT_SID'),
        flex: 1,
        editable: true
    },
    {
        field: "authToken",
        headerName: handleValidateCodevalue(internationalise, 'TC_COLUMN_AUTH_TOKEN'),
        flex: 1,
        editable: true
    },
    {
        field: "apiKey",
        headerName: handleValidateCodevalue(internationalise, 'TC_COLUMN_API_KEY'),
        flex: 1,
        editable: true
    },
    {
        field: "apiSecret",
        headerName: handleValidateCodevalue(internationalise, 'TC_COLUMN_API_SECRET'),
        flex: 1,
        editable: true
    },
    {
        field: "twimlAppSid",
        headerName: handleValidateCodevalue(internationalise, 'TC_COLUMN_TWIML_APP_SID'),
        flex: 1,
        editable: true
    },
    {
        field: "phoneNumber",
        headerName: handleValidateCodevalue(internationalise, 'COMMON_PHONE_NUMBER'),
        flex: 1,
        editable: true,
    }
  ], [rowModesModel, canEdit, internationalise]);

  return (
    <BiztantuLoader isChildren open={isLoading}>
      <StyledLeadContainer>
        <StyledConfigurationContainer className="provider-settings-header">
          <Typography variant='h6'>
            {handleValidateCodevalue(internationalise, 'TELECALLING_SETTINGS_TITLE')}
          </Typography>
        </StyledConfigurationContainer>
      </StyledLeadContainer>

      <Box className="hide-scrollbar provider-settings-content">
        <StyledSubHeader className="provider-settings-subheader">
          {handleValidateCodevalue(internationalise, 'SET_TC_PROVIDER_DETAILS')}
        </StyledSubHeader>

        <DataGridTable
          height='200px'
          rowModesModel={rowModesModel}
          columns={columns}
          isRequiredToolBar={false}
          isCheckboxReq = {false}
          dataGridData={dataGridData}
          handleProcessRowUpdate={processRowUpdate}
          hideFooter
        />
      </Box>
    </BiztantuLoader>
  );
};

export default TelecallingSettings;